import { isObject } from 'lodash';
import { CapTableDetailsModel, CompGroupListCreate } from 'api';
import { securityTypes } from 'common/constants';
import * as ALLOCATIONS from 'common/constants/allocations';
import * as CALIBRATION from 'common/constants/calibration';
import * as CAPTABLE from 'common/constants/cap-table';
import { FUND_ALIAS, SECURITY_ALIAS } from 'common/constants/fund-ownership';
import { SELECT_FUND } from 'common/constants/funds';
import * as inputOptions from 'common/constants/inputs';
import { CapTableVersion } from 'common/types/company';
import { Fund } from 'common/types/fund';
import * as CONVERTIBLE_NOTES from 'pages/CapTable/cap-table/components/convertible-notes-ledger/config/constants';
import { fundOptions, securityOptions, seniorityOptions } from 'pages/CapTable/cap-table/utilities/getOptionsForSelect';
import { VERSIONS_ALIAS } from 'pages/CompGroups/constants/constants';
import { SELECTION } from 'pages/Valuations/approaches/guidelinePublicCompanies/constants';
import { Approach } from 'pages/Valuations/approaches/guidelinePublicCompanies/types';
import { EQUITY_ALLOCATION_SPREADSHEET_CAP_TABLE_SELECTION } from 'pages/ValuationsAllocation/common/constants/equityAllocation/sheetAliases';
import { FE_ALLOCATION_METHOD_SPREADSHEET_CAP_TABLE } from 'pages/ValuationsAllocation/common/constants/futureExit/sheetAliases';
import { gridShortDate } from 'utilities';
import { OptionItem, OptionListType, PropsType } from './utilities.props';

const getOptionsForSecurityType = (optionList: string[], optionTypes: number[]) => {
  const tmpOptions: OptionListType = [];
  if (Array.isArray(optionList)) {
    optionTypes.forEach(type => {
      tmpOptions.push({
        value: type,
        label: optionList[type - 1],
      });
    });
  }
  return tmpOptions;
};

export const getOptionsFrom = (optionList: OptionListType | string[]) => {
  const tmpOptions: OptionListType = [];
  if (!optionList) {
    return tmpOptions;
  }

  if (Array.isArray(optionList)) {
    optionList.forEach((option, index) => {
      let parsedOption = null;

      // If the option is not an object, it is a string
      if (!isObject(option)) {
        parsedOption = {
          value: index,
          label: option,
        };
      } else {
        parsedOption = option;
      }

      if (parsedOption) {
        tmpOptions.push(parsedOption as OptionItem);
      }
    });
  }

  return tmpOptions;
};

/* Handlers for specific aliases */
type HandleVersionsAliasType = { group: CompGroupListCreate };
const handleVersionsAlias = ({ group }: HandleVersionsAliasType) => {
  const optionList: OptionListType = [];
  if (Array.isArray(group?.versions)) {
    group.versions.forEach(version => {
      optionList.push({
        label: `${gridShortDate(version.created_at)} | V-${version.version_number}`,
        value: version.id,
      });
    });
    const defaultLabel = group.versions.find(version => version.id === version.latest_version)?.name;
    return { optionList, defaultLabel };
  }

  return {
    optionList,
    defaultLabel: inputOptions.SELECT_OPTION,
  };
};

const handleSecurityTypeAlias = () => ({
  optionList: getOptionsForSecurityType(securityTypes.LIST, securityTypes.TYPES),
  defaultLabel: inputOptions.SELECT_SECURITY,
});

type HandleUnderlyingSecurityAliasType = { optionsFromCell: OptionItem };
const handleUnderlyingSecurityAlias = ({ optionsFromCell }: HandleUnderlyingSecurityAliasType) => ({
  optionList: getOptionsFrom(optionsFromCell),
  defaultLabel: inputOptions.SELECT_SECURITY,
});

type HandleSeniorityAliasType = { props: PropsType };
const handleSeniorityAlias = ({ props }: HandleSeniorityAliasType) => ({
  defaultLabel: CAPTABLE.SELECT_PRIORITY,
  optionList: seniorityOptions(props),
});

type HandleFundAliasType = { fundList: Fund[] };
const handleFundAlias = ({ fundList }: HandleFundAliasType) => ({
  optionList: fundOptions(fundList),
  defaultLabel: SELECT_FUND,
});

type HandleSecurityAliasType = { captableInfo: CapTableDetailsModel };
const handleSecurityAlias = ({ captableInfo }: HandleSecurityAliasType) => ({
  optionList: securityOptions(captableInfo),
  defaultLabel: inputOptions.SELECT_SECURITY,
});

type HandleCapTableAliasType = { capTableList: CapTableVersion[] };

const handleCapTableAlias = ({ capTableList }: HandleCapTableAliasType) => {
  if (capTableList) {
    const optionList: OptionListType = [];
    capTableList.forEach(capTable => {
      optionList.push({
        value: capTable.id,
        label: capTable.name,
      });
    });
    const defaultLabel = capTableList.find(item => item.is_primary)?.name;
    return { optionList, defaultLabel };
  }
  return {};
};

const handleDividendPaymentTypeAlias = () => ({
  optionList: getOptionsFrom(inputOptions.DIVIDEND_PAYMENT_TYPE),
  defaultLabel: inputOptions.SELECT_OPTION,
});

type HandleSelectionAliasType = { props: PropsType };
const handleSelectionAlias = ({ props }: HandleSelectionAliasType) => {
  const optionList = getOptionsFrom(props.options as OptionListType);
  return { optionList, defaultLabel: inputOptions.SELECT_OPTION };
};

const handleConvNoteEquityConversionModelAlias = () => ({
  optionList: getOptionsFrom(CONVERTIBLE_NOTES.EQUITY_CONVERSION_MODEL_VALUES),
  defaultLabel: inputOptions.SELECT_OPTION,
});
/* End Handlers for specific aliases */

type HandleApproachesValuationAliasType = { props: PropsType };
const handleApproachesValuationAlias = ({ props }: HandleApproachesValuationAliasType) => {
  const optionList: OptionListType = [];
  props.cell.sheet?.tableData?.approaches?.forEach(approach => {
    optionList.push({
      value: approach.id ?? approach.panelId,
      label: approach.name,
    });
  });

  return { optionList, defaultLabel: inputOptions.SELECT_APPROACH };
};

type HandleFinancialsVersionAliasType = { props: PropsType };
const handleFinancialsVersionAlias = ({ props }: HandleFinancialsVersionAliasType) => {
  const optionList: OptionListType = [];
  (props.options as Approach[]).forEach(approach => {
    optionList.push({
      value: approach.id,
      label: approach.name,
    });
  });
  optionList.push({
    value: 0,
    label: 'Specify Financials Metrics',
  });
  return { optionList, defaultLabel: inputOptions.SELECT_OPTION };
};

export const aliasesForGenericOptions = [
  CAPTABLE.PARTICIPATION_ALIAS, // 16.  Participating
  CAPTABLE.IS_PARTICIPATION_CAP_ALIAS, // 17.  Participation Cap
  CAPTABLE.CUMULATIVE_DIVIDENDS_ALIAS, // 19.  Cumulative Dividends
  CAPTABLE.HAS_MULTIPLE_INVESTMENT_DATES_ALIAS, // 20.  Multiple Investment Dates?
  CAPTABLE.COMPOUNDING_ALIAS, // 23.  Compounding
  CONVERTIBLE_NOTES.COMPOUNDING_ALIAS,
];

export const handlers = {
  /* These handlers update the default label AND the option list */
  [ALLOCATIONS.CAP_TABLE_ALIAS]: handleCapTableAlias,
  [FE_ALLOCATION_METHOD_SPREADSHEET_CAP_TABLE]: handleCapTableAlias,
  [CAPTABLE.SECURITY_TYPE_ALIAS]: handleSecurityTypeAlias,
  [CAPTABLE.SENIORITY_ALIAS]: handleSeniorityAlias,
  [CAPTABLE.UNDERLYING_SECURITY_ALIAS]: handleUnderlyingSecurityAlias,
  [EQUITY_ALLOCATION_SPREADSHEET_CAP_TABLE_SELECTION]: handleCapTableAlias,
  [FUND_ALIAS]: handleFundAlias,
  [SECURITY_ALIAS]: handleSecurityAlias,
  [VERSIONS_ALIAS]: handleVersionsAlias,
  /* The following handlers only update the option list */
  [CAPTABLE.DIVIDEND_PAYMENT_TYPE_ALIAS]: handleDividendPaymentTypeAlias,
  [CONVERTIBLE_NOTES.EQUITY_CONVERSION_MODEL_ALIAS]: handleConvNoteEquityConversionModelAlias,
  [SELECTION]: handleSelectionAlias,
  [CALIBRATION.PUBLIC_COMPS_APPROACH]: handleApproachesValuationAlias,
  [CALIBRATION.FINANCIALS_VERSION_CALIBRATION]: handleFinancialsVersionAlias,
};
